import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  ActivityIndicator,
  Keyboard,
  Platform,
  TouchableOpacity,
  TouchableWithoutFeedback,
  Text,
  StyleSheet,
  FlatList,
} from 'react-native';
import {RFValue} from 'react-native-responsive-fontsize';
import {getAnalytics, logEvent} from '@react-native-firebase/analytics';
import * as geolib from 'geolib';
import {version} from '../../../../package.json';
import type {StationDTO} from '../../../dtos/StationDTO';
import type {RootStackParamList} from '../../../dtos/RoutesDTO';
import {useLocalization} from '../../../context/localization';
import {apiv2} from '../../../services/api';
import {rastrear, STATUS_BY_TIPO_LISTAGEM} from '../../../services/rastrear';

import {Container, Content, FilterOptions, Section} from './styles';

import {NotFound} from '../../../components/Interface/Search/NotFound';
import {Titles} from '../../../components/Interface/Typographies/Titles';
import {SearchInput} from '../../../components/Interface/Forms/Inputs/SearchInput';
import {StationItem} from '../../../components/shared/Stations/StationItem';
import {FilterStationsCategory} from '../../../components/shared/FilterStationsCategory';

import {Header} from '../../../components/Interface/Headers/Header';
import {useTutorial} from '../../../context/TutorialProvider';

import {getPersistentDeviceId} from '../../../utils/deviceId';
import {NativeStackScreenProps} from 'react-native-screens/lib/typescript/native-stack/types';
import {LocationPillContainer} from '../../../components/shared/LocationPill';
import shieldIcon from '../../../assets/images/icons/shield.webp';
import {SafeAreaView} from 'react-native-safe-area-context';
import {PromoMarqueeModal} from '../../../components/shared/PromoMarqueeModal';
import {useIsFocused} from '@react-navigation/native';

type ScreenProps = NativeStackScreenProps<RootStackParamList, 'ListagemPostos'>;

const PAGE_SIZE = 10;

export function ListagemPostos({navigation, route}: ScreenProps) {
  const [isPromoModalOpen, setIsPromoModalOpen] = useState(false);

  const {cidade, tipo} = route.params;
  const {position, locationIsActive} = useLocalization();
  const {isActive} = useTutorial();

  const promoList = [
    {
      id: 3,
      text: 'Preço imbatível no Brasil. Não gostou? Estorno imediato!',
      icon: shieldIcon,
      action: () => setIsPromoModalOpen(true),
    },
  ];

  /* ----------------------------- estados ---------------------------- */
  const [stations, setStations] = useState<StationDTO[]>([]);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filter, setFilter] = useState<'ouro' | 'prata' | undefined>();

  /* loaders */

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isPaginating, setIsPaginating] = useState(false);
  const didInitialLoad = useRef(false);

  /* paginação */
  const [, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  // Refs para evitar closures "stale" no onEndReached
  const pageRef = useRef(1);
  const hasMoreRef = useRef(true);
  const isPaginatingRef = useRef(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // Aguarda 500ms após o usuário parar de digitar

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const allStationExceptNearStation = useCallback(
    (list: StationDTO[], nearId: string) => {
      return list.filter(({id}) => id !== nearId);
    },
    [],
  );

  const logSearchEvent = useCallback(() => {
    if (Platform.OS === 'web') {
      return;
    }

    try {
      const analytics =
        typeof getAnalytics === 'function' ? getAnalytics() : null;
      if (analytics && typeof logEvent === 'function') {
        logEvent(analytics, `busca_estabelecimentos_tipo_${tipo}`);
      }
    } catch {
      // noop: analytics shouldn't break listing on web/dev
    }
  }, [tipo]);

  const fetchStations = useCallback(
    async (pageToLoad: number, reset = false) => {
      isPaginatingRef.current = true;

      if (reset) {
        setIsRefreshing(true);
      } else {
        setIsPaginating(true);
      }

      try {
        logSearchEvent();

        const deviceId = await getPersistentDeviceId();

        const rota = !cidade
          ? `/stations2?tipoProduto=${tipo}&versao=${version}` +
            `&lat=${position?.latitude ?? ''}&lng=${
              position?.longitude ?? ''
            }` +
            `&page=${pageToLoad}&device_id=${deviceId}&app_version=${version}`
          : `/stations2?tipoProduto=${tipo}&cidade=${cidade}&versao=${version}&page=${pageToLoad}&device_id=${deviceId}&app_version=${version}`;

        const {data}: {data: any[]} = await apiv2.get(rota);

        if (!data.length || data.length < PAGE_SIZE) {
          setHasMore(false);
          hasMoreRef.current = false;
        }

        const formatted: StationDTO[] = await Promise.all(
          data.map(async item => {
            return {
              ...item,
              distance: position
                ? geolib
                    .convertDistance(
                      geolib.getDistance(
                        {
                          latitude: position.latitude,
                          longitude: position.longitude,
                        },
                        {latitude: item.lat, longitude: item.lng},
                      ),
                      'km',
                    )
                    .toFixed(1)
                : undefined,
            };
          }),
        );

        setStations(prev => {
          const next =
            pageToLoad === 1 || reset
              ? formatted
              : [
                  ...prev,
                  ...formatted.filter(n => !prev.some(p => p.id === n.id)),
                ];

          return next;
        });
      } catch (err) {
        console.error('Erro ao buscar postos:', err);
      } finally {
        setIsRefreshing(false);
        setIsPaginating(false);
        isPaginatingRef.current = false;
        didInitialLoad.current = true;
      }
    },
    [tipo, cidade, position],
  );

  const fetchStationsRef = useRef(fetchStations);
  useEffect(() => {
    fetchStationsRef.current = fetchStations;
  }, [fetchStations]);

  useEffect(() => {
    if (!didInitialLoad.current) {
      return;
    }

    if (debouncedSearchTerm.trim().length > 0) {
      return;
    }

    setPage(1);
    pageRef.current = 1;
    setHasMore(true);
    hasMoreRef.current = true;
    fetchStationsRef.current(1, true);
  }, [debouncedSearchTerm]);

  const isFocused = useIsFocused();

  const normalizeText = useCallback((value?: string) => {
    return String(value ?? '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }, []);

  useEffect(() => {
    setStations([]);
    setPage(1);
    pageRef.current = 1;
    setHasMore(true);
    hasMoreRef.current = true;

    fetchStations(1).then(() => {
      if (isFocused) {
        const status_tela = STATUS_BY_TIPO_LISTAGEM[tipo];
        if (status_tela) {
          rastrear({status_tela, cidade});
        }
      }
    });
  }, [isFocused]);

  const handleSearchStations = (text: string) => {
    setSearchTerm(text);
  };
  const handleFilter = (cat: 'ouro' | 'prata' | undefined) => {
    setFilter(cat);
  };

  const hasCategories = useMemo(
    () =>
      stations.some(s => s.categoria === 'ouro') &&
      stations.some(s => s.categoria === 'prata'),
    [stations],
  );

  const filteredStations = useMemo(() => {
    let base = stations;

    if (filter) {
      base = base.filter(s => s.categoria === filter);
    }

    const withAvailability = isActive
      ? base.filter(s => !s.indisponivel)
      : base;

    const term = normalizeText(debouncedSearchTerm.trim());
    if (!term) {
      return withAvailability;
    }

    return withAvailability.filter(s => {
      const name = normalizeText(s.name);
      const neighborhood = normalizeText(s.neighbourhood);
      const flag = normalizeText(s.flag);
      return (
        name.includes(term) ||
        neighborhood.includes(term) ||
        flag.includes(term)
      );
    });
  }, [stations, filter, isActive, debouncedSearchTerm, normalizeText]);

  const stationsToRender = useMemo(() => {
    if (position && !debouncedSearchTerm && filteredStations.length > 0) {
      return filteredStations;
    }

    return filteredStations;
  }, [
    filteredStations,
    debouncedSearchTerm,
    position,
    allStationExceptNearStation,
  ]);

  const loadMore = useCallback(() => {
    if (isPaginatingRef.current || !hasMoreRef.current) {
      return;
    }

    const next = pageRef.current + 1;
    pageRef.current = next;
    setPage(next);
    fetchStations(next);
  }, [fetchStations]);

  useEffect(() => {
    const term = debouncedSearchTerm.trim();
    if (!term) {
      return;
    }
    if (stations.length === 0) {
      return;
    }
    if (isPaginatingRef.current || !hasMoreRef.current) {
      return;
    }
    loadMore();
  }, [debouncedSearchTerm, stations.length, loadMore]);

  const onScroll = useCallback(
    ({nativeEvent}: any) => {
      const {layoutMeasurement, contentOffset, contentSize} = nativeEvent;
      const distanceFromEnd =
        contentSize.height - layoutMeasurement.height - contentOffset.y;

      // Dispara paginação automática quando falta menos de 50px  pro fim
      if (distanceFromEnd < 50) {
        loadMore();
      }
    },
    [loadMore],
  );

  const showNotFound = useMemo(
    () =>
      !debouncedSearchTerm &&
      stations.length === 0 &&
      didInitialLoad.current &&
      !isRefreshing &&
      !isPaginating,
    [debouncedSearchTerm, stations.length, isRefreshing, isPaginating],
  );

  return (
    <TouchableWithoutFeedback
      onPress={() => Platform.OS !== 'web' && Keyboard.dismiss()}
      accessible={false}>
      <SafeAreaView edges={['bottom']} style={{flex: 1}}>
        <Container>
          <Header
            icon="chevron-left"
            parentHelp
            showPromotionMarquee
            promoMarqueeData={promoList}
            action={() => navigation.goBack()}
          />

          <PromoMarqueeModal
            isOpen={isPromoModalOpen}
            onClose={() => setIsPromoModalOpen(false)}
          />

          {!showNotFound ? (
            <Content>
              <Titles
                title={tipo === 'C' ? 'Postos da região' : 'Pontos de retirada'}
              />

              <Section>
                <SearchInput
                  placeholder="Buscar por nome, bairro ou bandeira"
                  value={searchTerm}
                  onChangeText={handleSearchStations}
                />

                {Platform.OS === 'web' ? (
                  position && (
                    <LocationPillContainer
                      cidade={cidade}
                      tipo={tipo}
                      onPressUserLocationCallback={() => {
                        setPage(1);
                        pageRef.current = 1;
                        setHasMore(true);
                        hasMoreRef.current = true;
                        fetchStations(1, true); // refresh
                      }}
                    />
                  )
                ) : (
                  <LocationPillContainer
                    cidade={cidade}
                    tipo={tipo}
                    onPressUserLocationCallback={() => {
                      setPage(1);
                      pageRef.current = 1;
                      setHasMore(true);
                      hasMoreRef.current = true;
                      fetchStations(1, true); // refresh
                    }}
                  />
                )}

                {!debouncedSearchTerm && hasCategories && (
                  <FilterOptions>
                    <FilterStationsCategory
                      onPress={() => handleFilter(undefined)}
                      title="Todos"
                    />

                    <FilterStationsCategory
                      rank="gold"
                      onPress={() => handleFilter('ouro')}
                      title="Super Barato"
                    />

                    <FilterStationsCategory
                      rank="silver"
                      onPress={() => handleFilter('prata')}
                      title="Barato"
                      mr={0}
                    />
                  </FilterOptions>
                )}

                <FlatList
                  style={{
                    marginHorizontal: -24,
                    flex: 1,
                    marginTop: RFValue(16),
                  }}
                  data={stationsToRender}
                  keyExtractor={item => item.id}
                  renderItem={({item, index}) =>
                    renderStationItem(item, index, tipo, locationIsActive)
                  }
                  showsVerticalScrollIndicator={false}
                  nestedScrollEnabled={false}
                  contentContainerStyle={{
                    paddingBottom: 20,
                    flexGrow: 1,
                  }}
                  onScroll={onScroll}
                  scrollEventThrottle={100}
                  ListFooterComponent={
                    <>
                      {(isPaginating || isRefreshing) && (
                        <ActivityIndicator
                          style={{marginVertical: RFValue(16)}}
                          size="small"
                          color="#999"
                        />
                      )}
                      {hasMore &&
                        !isPaginating &&
                        !isRefreshing &&
                        stationsToRender.length > 0 && (
                          <TouchableOpacity
                            style={loadMoreStyles.button}
                            activeOpacity={0.7}
                            onPress={loadMore}>
                            <Text style={loadMoreStyles.text}>
                              Ver mais postos
                            </Text>
                          </TouchableOpacity>
                        )}
                    </>
                  }
                />
              </Section>
            </Content>
          ) : (
            <NotFound
              style={{flex: 1, width: '100%'}}
              title={`Nenhum posto/produto encontrado ${
                cidade ? 'em ' + cidade : 'na sua região'
              }`}
              text="Não encontramos nenhum posto ou produtos para a região selecionada."
              onRetry={() => navigation.navigate('Localization', {tipo})}
            />
          )}
        </Container>
      </SafeAreaView>
    </TouchableWithoutFeedback>
  );
}

function renderStationItem(
  station: StationDTO,
  index: number,
  tipo: string,
  isUserLocation: boolean,
) {
  const firstNoNear = index === 0;
  const common = {
    data: station,
    showRank: true,

    showFuelPrices: tipo === 'C',
    tipo,
    isUserLocation,
  };

  return firstNoNear ? (
    <StationItem
      {...common}
      stepOrder={7}
      stepText="Selecione o posto que desejar"
      stepOnPress={() => {}}
    />
  ) : (
    <StationItem {...common} />
  );
}

const loadMoreStyles = StyleSheet.create({
  button: {
    alignSelf: 'center',
    paddingVertical: RFValue(10),
    paddingHorizontal: RFValue(24),
    borderRadius: RFValue(8),
    borderWidth: 1,
    borderColor: '#68ccde',
    marginVertical: RFValue(12),
  },
  text: {
    color: '#68ccde',
    fontSize: RFValue(13),
    fontWeight: '600',
  },
});
